import type { GradingId } from "@repo/app-types/entities/teammatePerformanceSummary";
import type { GradingReferenceWrapper } from "@repo/ui/structuredContent/gradingReferenceContext";
import { InteractiveQuote } from "@repo/ui/structuredContent/interactiveQuote";
import { useCallback } from "react";
import { GradingReferenceLinkAdapter } from "./gradingReferenceLinkAdapter.js";

/**
 * Builds the grading-reference wrapper for a surface that already has one
 * interaction on screen. A quote from that interaction scrolls the open
 * transcript to it; anything else — another call, or a reference with no
 * quoted text — falls through to the link adapter and opens in a new tab.
 *
 * The shared renderer shows its external-link affordance only under an
 * anchor, so the in-place button carries none.
 */
export function useOpenInteractionGradingReferenceWrapper(options: {
  openGradingId: GradingId | undefined;
  onQuoteFocus: (quote: string) => void;
}): GradingReferenceWrapper {
  const { openGradingId, onQuoteFocus } = options;

  return useCallback<GradingReferenceWrapper>(
    ({ reference, className, children }) => {
      const quote = reference.quote;
      if (!quote || !openGradingId || reference.gradingId !== openGradingId) {
        return (
          <GradingReferenceLinkAdapter
            reference={reference}
            className={className}
          >
            {children}
          </GradingReferenceLinkAdapter>
        );
      }

      return (
        <InteractiveQuote
          className={className}
          onPress={() => onQuoteFocus(quote)}
          quote={quote}
        >
          {children}
        </InteractiveQuote>
      );
    },
    [openGradingId, onQuoteFocus],
  );
}
